/**
 * Sales Flywheel Tab - Opportunity State Machine UI
 * 
 * "An opportunity is not real until it can be pursued, contacted, 
 * proposed, and closed inside the system."
 * 
 * This is the revenue bridge from detection to contract.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  PlayArrow as PursueIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Description as ProposalIcon,
  EmojiEvents as WinIcon,
  Cancel as LoseIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import stratagemService, {
  SalesOpportunity,
  OpportunityStats,
  OpportunityState,
} from '../../services/stratagemService';

// State colors matching the pipeline stages
const stateColors: Record<OpportunityState, string> = {
  detected: '#9e9e9e',      // Gray - just found
  pursued: '#2196f3',        // Blue - actively working
  outreach_sent: '#ff9800',  // Orange - waiting for response
  engaged: '#4caf50',        // Green - in conversation
  proposal_sent: '#9c27b0',  // Purple - proposal out
  contract_won: '#00c853',   // Bright green - won!
  contract_lost: '#f44336',  // Red - lost
};

const stateLabels: Record<OpportunityState, string> = {
  detected: 'Detected',
  pursued: 'Pursued',
  outreach_sent: 'Outreach Sent',
  engaged: 'Engaged',
  proposal_sent: 'Proposal Sent',
  contract_won: 'Won',
  contract_lost: 'Lost',
};

// Valid next states from each state
const nextActions: Record<OpportunityState, { action: string; icon: React.ReactNode; nextState: OpportunityState }[]> = {
  detected: [
    { action: 'Pursue', icon: <PursueIcon />, nextState: 'pursued' },
    { action: 'Mark Lost', icon: <LoseIcon />, nextState: 'contract_lost' },
  ],
  pursued: [
    { action: 'Send Outreach', icon: <EmailIcon />, nextState: 'outreach_sent' },
    { action: 'Mark Lost', icon: <LoseIcon />, nextState: 'contract_lost' },
  ],
  outreach_sent: [
    { action: 'Log Engagement', icon: <PhoneIcon />, nextState: 'engaged' },
    { action: 'Mark Lost', icon: <LoseIcon />, nextState: 'contract_lost' },
  ],
  engaged: [
    { action: 'Send Proposal', icon: <ProposalIcon />, nextState: 'proposal_sent' },
    { action: 'Mark Lost', icon: <LoseIcon />, nextState: 'contract_lost' },
  ],
  proposal_sent: [
    { action: 'Mark Won', icon: <WinIcon />, nextState: 'contract_won' },
    { action: 'Mark Lost', icon: <LoseIcon />, nextState: 'contract_lost' },
  ],
  contract_won: [],
  contract_lost: [],
};

interface SalesFlywheelTabProps {
  onNavigateToDetail?: (id: string) => void;
}

const SalesFlywheelTab: React.FC<SalesFlywheelTabProps> = ({ onNavigateToDetail }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [opportunities, setOpportunities] = useState<SalesOpportunity[]>([]);
  const [stats, setStats] = useState<OpportunityStats | null>(null);
  const [filterState, setFilterState] = useState<string>('all');
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    opportunity: SalesOpportunity | null;
    actionType: string;
    nextState: OpportunityState | null;
  }>({ open: false, opportunity: null, actionType: '', nextState: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state for dialogs
  const [formData, setFormData] = useState<Record<string, any>>({});
  
  // Load data
  useEffect(() => {
    loadData();
  }, [filterState]);
  
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [opps, statsData] = await Promise.all([
        stratagemService.getOpportunities(filterState !== 'all' ? { state: filterState } : undefined),
        stratagemService.getOpportunityStats(),
      ]);
      setOpportunities(opps);
      setStats(statsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAction = (opp: SalesOpportunity, actionType: string, nextState: OpportunityState) => {
    setFormData({});
    setActionDialog({ open: true, opportunity: opp, actionType, nextState });
  };
  
  const executeAction = async () => {
    if (!actionDialog.opportunity || !actionDialog.nextState) return;
    
    setActionLoading(true);
    try {
      const id = actionDialog.opportunity.id;
      
      switch (actionDialog.nextState) {
        case 'pursued':
          await stratagemService.pursueOpportunity(id, {
            priority: formData.priority || 'medium',
            notes: formData.notes,
          });
          break;
        case 'outreach_sent':
          await stratagemService.launchOutreach(id, {
            channel: formData.channel || 'email',
            template_id: formData.template_id,
            custom_message: formData.custom_message,
            contact_email: formData.contact_email,
            contact_name: formData.contact_name,
          });
          break;
        case 'engaged':
          await stratagemService.logEngagement(id, {
            engagement_type: formData.engagement_type || 'reply',
            engagement_notes: formData.engagement_notes || 'Engagement logged',
            next_action: formData.next_action,
          });
          break;
        case 'proposal_sent':
          await stratagemService.sendProposal(id, {
            proposal_title: formData.proposal_title || 'Service Proposal',
            proposal_amount: parseFloat(formData.proposal_amount) || 0,
            proposal_terms: formData.proposal_terms,
          });
          break;
        case 'contract_won':
          await stratagemService.winContract(id, {
            contract_value: parseFloat(formData.contract_value) || 0,
            contract_terms: formData.contract_terms,
          });
          break;
        case 'contract_lost':
          await stratagemService.loseContract(id, {
            loss_reason: formData.loss_reason || 'Not specified',
            notes: formData.notes,
          });
          break;
      }
      
      setActionDialog({ open: false, opportunity: null, actionType: '', nextState: null });
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };
  
  const formatCurrency = (value: number | string | null) => {
    if (!value) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);
  };
  
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  
  const getSourceLabel = (opp: SalesOpportunity) => {
    if (opp.title) return opp.title;
    if (opp.contact_company) return opp.contact_company;
    if (opp.detection_rationale?.company_name) return opp.detection_rationale.company_name;
    return opp.source_engine;
  };
  
  // Render action dialog content based on action type
  const renderDialogContent = () => {
    switch (actionDialog.nextState) {
      case 'pursued':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority || 'medium'}
                label="Priority"
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes"
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </>
        );
      case 'outreach_sent':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Channel</InputLabel>
              <Select
                value={formData.channel || 'email'}
                label="Channel"
                onChange={(e) => setFormData({ ...formData, channel: e.target.value })}
              >
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="linkedin">LinkedIn</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Contact Name"
              value={formData.contact_name || ''}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
            />
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Contact Email"
              value={formData.contact_email || ''}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Custom Message (optional)"
              value={formData.custom_message || ''}
              onChange={(e) => setFormData({ ...formData, custom_message: e.target.value })}
            />
          </>
        );
      case 'engaged':
        return (
          <>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Engagement Type</InputLabel>
              <Select
                value={formData.engagement_type || 'reply'}
                label="Engagement Type"
                onChange={(e) => setFormData({ ...formData, engagement_type: e.target.value })}
              >
                <MenuItem value="reply">Email Reply</MenuItem>
                <MenuItem value="call">Phone Call</MenuItem>
                <MenuItem value="meeting">Meeting</MenuItem>
                <MenuItem value="demo">Demo</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Engagement Notes"
              required
              value={formData.engagement_notes || ''}
              onChange={(e) => setFormData({ ...formData, engagement_notes: e.target.value })}
            />
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label="Next Action"
              value={formData.next_action || ''}
              onChange={(e) => setFormData({ ...formData, next_action: e.target.value })}
            />
          </>
        );
      case 'proposal_sent':
        return (
          <>
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Proposal Title"
              required
              value={formData.proposal_title || ''}
              onChange={(e) => setFormData({ ...formData, proposal_title: e.target.value })}
            />
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              type="number"
              label="Proposal Amount ($)"
              required
              value={formData.proposal_amount || ''}
              onChange={(e) => setFormData({ ...formData, proposal_amount: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Terms & Conditions"
              value={formData.proposal_terms || ''}
              onChange={(e) => setFormData({ ...formData, proposal_terms: e.target.value })}
            />
          </>
        );
      case 'contract_won':
        return (
          <>
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              type="number"
              label="Contract Value ($)"
              required
              value={formData.contract_value || ''}
              onChange={(e) => setFormData({ ...formData, contract_value: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Contract Terms"
              value={formData.contract_terms || ''}
              onChange={(e) => setFormData({ ...formData, contract_terms: e.target.value })}
            />
          </>
        );
      case 'contract_lost':
        return (
          <>
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Loss Reason"
              required
              value={formData.loss_reason || ''}
              onChange={(e) => setFormData({ ...formData, loss_reason: e.target.value })}
            />
            <TextField
              fullWidth
              sx={{ mb: 2 }}
              label="Competitor (if known)"
              value={formData.competitor_won || ''}
              onChange={(e) => setFormData({ ...formData, competitor_won: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Additional Notes"
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </>
        );
      default:
        return null;
    }
  };
  
  return (
    <Box>
      {/* Header Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: alpha(theme.palette.primary.main, 0.1), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TimelineIcon color="primary" />
                <Typography variant="body2" color="text.secondary">Pipeline</Typography>
              </Box>
              <Typography variant="h4" fontWeight={700}>
                {loading ? <Skeleton width={60} /> : stats?.total || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">Total Opportunities</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: alpha(theme.palette.warning.main, 0.1), border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MoneyIcon sx={{ color: 'warning.main' }} />
                <Typography variant="body2" color="text.secondary">Pipeline Value</Typography>
              </Box>
              <Typography variant="h4" fontWeight={700}>
                {loading ? <Skeleton width={80} /> : formatCurrency(stats?.total_value || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Estimated Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: alpha(theme.palette.success.main, 0.1), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WinIcon sx={{ color: 'success.main' }} />
                <Typography variant="body2" color="text.secondary">Won Value</Typography>
              </Box>
              <Typography variant="h4" fontWeight={700}>
                {loading ? <Skeleton width={80} /> : formatCurrency(stats?.won_value || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Closed Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: alpha(theme.palette.info.main, 0.1), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SpeedIcon sx={{ color: 'info.main' }} />
                <Typography variant="body2" color="text.secondary">Win Rate</Typography>
              </Box>
              <Typography variant="h4" fontWeight={700}>
                {loading ? <Skeleton width={60} /> : `${((stats?.win_rate || 0) * 100).toFixed(0)}%`}
              </Typography>
              <Typography variant="caption" color="text.secondary">Conversion</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Pipeline Stages Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Pipeline Stages
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={`All (${stats?.total || 0})`}
              variant={filterState === 'all' ? 'filled' : 'outlined'}
              onClick={() => setFilterState('all')}
            />
            {Object.entries(stateLabels).map(([state, label]) => (
              <Chip
                key={state}
                label={`${label} (${stats?.by_state?.[state as OpportunityState] || 0})`}
                variant={filterState === state ? 'filled' : 'outlined'}
                onClick={() => setFilterState(state)}
                sx={{
                  borderColor: stateColors[state as OpportunityState],
                  ...(filterState === state && {
                    backgroundColor: stateColors[state as OpportunityState],
                    color: 'white',
                  }),
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>
      
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Actions Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      
      {/* Opportunities Table */}
      <TableContainer component={Paper} variant="outlined">
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Opportunity</TableCell>
              <TableCell>Vertical/Region</TableCell>
              <TableCell align="center">State</TableCell>
              <TableCell align="center">Confidence</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell align="center">Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && opportunities.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <TrendingUpIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No opportunities in pipeline yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opportunities are auto-detected from Vertical Discovery, Federal Capture, and REV108 leads.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {opportunities.map((opp) => (
              <TableRow 
                key={opp.id} 
                hover
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {getSourceLabel(opp)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {opp.source_engine}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={opp.vertical_slug || opp.geo_region || 'general'} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={stateLabels[opp.state]}
                    size="small"
                    sx={{
                      backgroundColor: stateColors[opp.state],
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={(parseFloat(String(opp.confidence_score)) >= 0.8 ? 'HIGH' : 
                           parseFloat(String(opp.confidence_score)) >= 0.5 ? 'MEDIUM' : 'LOW')}
                    size="small"
                    color={
                      parseFloat(String(opp.confidence_score)) >= 0.8 ? 'success' :
                      parseFloat(String(opp.confidence_score)) >= 0.5 ? 'warning' : 'default'
                    }
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(opp.contract_value || opp.estimated_value)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(opp.created_at)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/stratagem/opportunity/${opp.id}`);
                        }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {nextActions[opp.state].slice(0, 2).map((action, idx) => (
                      <Tooltip key={idx} title={action.action}>
                        <IconButton
                          size="small"
                          color={action.nextState === 'contract_lost' ? 'error' : 'primary'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAction(opp, action.action, action.nextState);
                          }}
                        >
                          {action.icon}
                        </IconButton>
                      </Tooltip>
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Action Dialog */}
      <Dialog
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, opportunity: null, actionType: '', nextState: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionDialog.actionType}: {actionDialog.opportunity && getSourceLabel(actionDialog.opportunity)}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {renderDialogContent()}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setActionDialog({ open: false, opportunity: null, actionType: '', nextState: null })}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={executeAction}
            disabled={actionLoading}
            color={actionDialog.nextState === 'contract_lost' ? 'error' : 'primary'}
          >
            {actionLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesFlywheelTab;
